import Phaser from 'phaser'

import type {
  CampaignEventAction,
  CampaignEventCondition,
  CampaignEventDefinition,
  CampaignEventTrigger,
} from '../config/campaignAssets'
import { ACTIVE_CAMPAIGN_ID } from '../config/campaign'
import { getCampaignSfxAudioKey, getCampaignVoiceAudioKey } from '../config/campaignAssets'

export type EventHitArea = {
  x: number
  y: number
  halfWidth: number
  halfHeight: number
}

export type CampaignEventContext = {
  scene: Phaser.Scene
  getMapContainer: () => Phaser.GameObjects.Container | undefined
  getEventHitArea: (eventId: string) => EventHitArea | undefined
  getEventObject: (
    eventId: string
  ) => Phaser.GameObjects.Image | Phaser.GameObjects.Text | undefined
  getTileLocation: (eventId: string) => { row: number; col: number } | undefined
  getTileInstanceAt: (row: number, col: number) => { image: Phaser.GameObjects.Image } | undefined
  isEventTileVisible: (eventId: string) => boolean
  getCellMetrics: () => {
    mapRows: number
    mapCols: number
    maxCellWidthPx: number
    maxCellHeightPx: number
  }
  setHint: (hint: string) => void
  stopModalAudio: () => void
  showDialogModal: (textKey: string, subtitle?: string) => void
  revealTile: (tileId: number) => void
  openDoor: (tileId: number) => void
  playVoice: (audioKey: string) => void
  playSfx: (audioKey: string) => void
  isGoalCompleted: () => boolean
  markGoalCompleted: () => void
  endGoal: (reasonKey: string) => void
  getFlag: (key: string) => boolean
  setFlag: (key: string, value: boolean) => void
}

export class CampaignEventManager {
  private context: CampaignEventContext
  private pendingSubtitle?: string

  constructor(context: CampaignEventContext) {
    this.context = context
  }

  public updateContext(context: CampaignEventContext): void {
    this.context = context
  }

  public findEventAt(worldX: number, worldY: number, events: CampaignEventDefinition[]): CampaignEventDefinition | undefined {
    const mapContainer = this.context.getMapContainer()
    const localPoint = mapContainer ? mapContainer.getLocalPoint(worldX, worldY) : undefined
    const { mapRows, mapCols, maxCellHeightPx, maxCellWidthPx } = this.context.getCellMetrics()

    let bestMatch: { event: CampaignEventDefinition; distance: number } | undefined

    const considerMatch = (
      eventDef: CampaignEventDefinition,
      centerX: number,
      centerY: number,
      halfWidth: number,
      halfHeight: number,
      useLocal: boolean
    ): void => {
      const point = useLocal ? localPoint : { x: worldX, y: worldY }

      if (!point) {
        return
      }

      const dx = point.x - centerX
      const dy = point.y - centerY

      if (Math.abs(dx) <= halfWidth && Math.abs(dy) <= halfHeight) {
        const distance = Math.abs(dx) + Math.abs(dy)

        if (!bestMatch || distance < bestMatch.distance) {
          bestMatch = { event: eventDef, distance }
        }
      }
    }

    for (const eventDef of events) {
      if (!this.context.isEventTileVisible(eventDef.id)) {
        continue
      }

      const hitArea = this.context.getEventHitArea(eventDef.id)

      if (localPoint && hitArea) {
        considerMatch(eventDef, hitArea.x, hitArea.y, hitArea.halfWidth, hitArea.halfHeight, true)
      }

      if (bestMatch?.event === eventDef) {
        continue
      }

      const displayObject = this.context.getEventObject(eventDef.id)

      if (displayObject && displayObject.visible) {
        const bounds = displayObject.getBounds()
        considerMatch(eventDef, bounds.centerX, bounds.centerY, bounds.width / 2, bounds.height / 2, false)
      }

      if (!bestMatch && !eventDef.token) {
        const location = this.context.getTileLocation(eventDef.id)

        if (location) {
          const tileInstance = this.context.getTileInstanceAt(location.row, location.col)

          if (tileInstance) {
            if (!tileInstance.image.visible) {
              continue
            }
            const bounds = tileInstance.image.getBounds()
            considerMatch(eventDef, bounds.centerX, bounds.centerY, bounds.width / 2, bounds.height / 2, false)
          } else if (localPoint && maxCellWidthPx > 0 && maxCellHeightPx > 0) {
            const localX = -((mapCols * maxCellWidthPx) / 2) + (location.col + 0.5) * maxCellWidthPx
            const localY = -((mapRows * maxCellHeightPx) / 2) + (location.row + 0.5) * maxCellHeightPx
            considerMatch(eventDef, localX, localY, maxCellWidthPx * 0.45, maxCellHeightPx * 0.45, true)
          }
        }
      }
    }

    return bestMatch?.event
  }

  public isTriggerSatisfied(trigger: CampaignEventTrigger | undefined): boolean {
    if (!trigger) {
      return true
    }

    switch (trigger.type) {
      case 'onInteract':
        return true
      default:
        console.log(`Trigger no soportado: "${trigger.type}"`)
        return false
    }
  }

  public executeActions(eventDef: CampaignEventDefinition): void {
    if (!this.areConditionsSatisfied(eventDef.conditions)) {
      return
    }

    this.executeActionList(eventDef.actions ?? [])
  }

  private areConditionsSatisfied(conditions: CampaignEventCondition[] | undefined): boolean {
    if (!conditions || conditions.length === 0) {
      return true
    }

    return conditions.every((condition) => {
      switch (condition.type) {
        case 'flagTrue':
          return typeof condition.key === 'string' && this.context.getFlag(condition.key)
        default:
          console.warn(`Condición no soportada: "${condition.type}"`)
          return false
      }
    })
  }

  private executeActionList(actions: CampaignEventAction[]): void {
    actions.forEach((action) => {
      switch (action.type) {
        case 'showDialog':
          this.handleShowDialog(action)
          break
        case 'revealArea':
          this.handleRevealArea(action)
          break
        case 'openDoor':
          this.handleOpenDoor(action)
          break
        case 'setFlag':
          this.handleSetFlag(action)
          break
        case 'requireDice':
          this.handleRequireDice(action)
          break
        case 'playVoice':
          this.handlePlayVoice(action)
          break
        case 'playSfx':
          this.handlePlaySfx(action)
          break
        case 'endGoal':
          this.handleEndGoal(action)
          break
        default:
          console.warn(`Acción no soportada: "${action.type}"`)
      }
    })
  }

  private handleShowDialog(action: CampaignEventAction): void {
    if (typeof action.textKey !== 'string' || action.textKey.trim().length === 0) {
      return
    }

    this.context.showDialogModal(action.textKey, this.consumePendingSubtitle())
  }

  private handleRevealArea(action: CampaignEventAction): void {
    if (typeof action.id !== 'string') {
      return
    }

    const tileId = Number.parseInt(action.id, 10)

    if (!Number.isFinite(tileId)) {
      console.log(`revealArea recibió id inválido: "${action.id}"`)
      return
    }

    this.context.revealTile(tileId)
  }

  private handleOpenDoor(action: CampaignEventAction): void {
    if (typeof action.id !== 'string') {
      return
    }

    const tileId = Number.parseInt(action.id, 10)

    if (!Number.isFinite(tileId)) {
      console.log(`openDoor recibió id inválido: "${action.id}"`)
      return
    }

    this.context.openDoor(tileId)
  }

  private handleSetFlag(action: CampaignEventAction): void {
    if (typeof action.key !== 'string' || action.key.trim().length === 0) {
      return
    }

    const value = typeof action.value === 'boolean' ? action.value : true
    this.context.setFlag(action.key, value)
  }

  private handleRequireDice(action: CampaignEventAction): void {
    const dcValue = Number(action.dc)

    if (!Number.isFinite(dcValue)) {
      return
    }

    const sidesValue = Number(action.sides)
    const diceSides = Number.isFinite(sidesValue) && sidesValue >= 2 ? Math.floor(sidesValue) : 6
    const roll = Phaser.Math.Between(1, diceSides)
    const success = roll >= dcValue

    this.context.setHint(`Tirada ${roll}/${diceSides} (DC ${dcValue})`)
    this.pendingSubtitle = `Tirada ${roll} / ${diceSides} (DC ${dcValue})`

    if (success && Array.isArray(action.onSuccess)) {
      this.executeActionList(action.onSuccess as CampaignEventAction[])
    } else if (!success && Array.isArray(action.onFail)) {
      this.executeActionList(action.onFail as CampaignEventAction[])
    } else {
      this.pendingSubtitle = undefined
    }
  }

  private handlePlayVoice(action: CampaignEventAction): void {
    if (typeof action.file !== 'string' || action.file.trim().length === 0) {
      return
    }

    const key = action.file.replace(/^vo\//, '').replace(/\.[^.]+$/, '')
    const audioKey = getCampaignVoiceAudioKey(ACTIVE_CAMPAIGN_ID, key)

    this.context.stopModalAudio()
    this.context.playVoice(audioKey)
  }

  private handlePlaySfx(action: CampaignEventAction): void {
    if (typeof action.file !== 'string' || action.file.trim().length === 0) {
      return
    }

    const key = action.file.replace(/^sfx\//, '').replace(/\.[^.]+$/, '')
    const audioKey = getCampaignSfxAudioKey(ACTIVE_CAMPAIGN_ID, key)

    this.context.stopModalAudio()
    this.context.playSfx(audioKey)
  }

  private handleEndGoal(action: CampaignEventAction): void {
    console.log('handleEndGoal',this.context.isGoalCompleted(),action)
    if (this.context.isGoalCompleted()) {
      return
    }

    const reasonKey = typeof action.reason === 'string' && action.reason.trim().length > 0 ? action.reason : 'mission.found'
    this.context.markGoalCompleted()
    this.context.endGoal(reasonKey)
  }

  private consumePendingSubtitle(): string | undefined {
    const subtitle = this.pendingSubtitle
    this.pendingSubtitle = undefined
    return subtitle
  }
}
